/**
 * BikeGapChart — time delta (gap) chart vs course distance with elevation overlay.
 *
 * Green area when A is ahead (B slower), red when B is ahead (B faster).
 */
import { useMemo } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  Tooltip,
} from 'recharts';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { tokens } from '../../../theme';

const COLOR_A = '#3b82f6';
const COLOR_B = '#f59e0b';

function formatTime(seconds) {
  if (seconds == null) return '—';
  const sign = seconds < 0 ? '−' : '+';
  const abs = Math.abs(seconds);
  const m = Math.floor(abs / 60);
  const s = Math.floor(abs % 60);
  return `${sign}${m}:${s.toString().padStart(2, '0')}`;
}

function formatSpeed(kph) {
  if (kph == null) return '—';
  return `${kph.toFixed(1)} km/h`;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload;
  return (
    <Box sx={{ bgcolor: '#1e293b', border: '1px solid #334155', borderRadius: 1, p: 1.5, fontSize: '0.8rem' }}>
      <Typography variant="caption" color="#94a3b8">{`${(label / 1000).toFixed(1)} km`}</Typography>
      <Box mt={0.5}>
        <Typography variant="body2" color="#e2e8f0">Δt {formatTime(row?.time_delta_s)}</Typography>
        <Typography variant="body2" color={COLOR_A}>A: {formatSpeed(row?.speed_a)}</Typography>
        <Typography variant="body2" color={COLOR_B}>B: {formatSpeed(row?.speed_b)}</Typography>
        {row?.altitude_a != null && (
          <Typography variant="body2" color="#94a3b8">Alt: {Math.round(row.altitude_a)} m</Typography>
        )}
      </Box>
    </Box>
  );
};

export default function BikeGapChart({ resampled }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const data = useMemo(() => {
    if (!resampled || resampled.length === 0) return [];
    return resampled.map((row) => ({
      ...row,
      gap_pos: row.time_delta_s > 0 ? row.time_delta_s : 0,
      gap_neg: row.time_delta_s < 0 ? row.time_delta_s : 0,
    }));
  }, [resampled]);

  if (!data.length) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height={300}>
        <Typography color={colors.grey[400]}>No alignment data available</Typography>
      </Box>
    );
  }

  const altValues = data.map(r => r.altitude_a).filter(v => v != null);
  const altMin = Math.min(...altValues);
  const altMax = Math.max(...altValues);

  return (
    <Box>
      <Typography variant="caption" color={colors.grey[400]} mb={1} display="block">
        Time gap (positive = A faster than B)
      </Typography>
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={data} margin={isMobile ? { top: 5, right: 5, bottom: 5, left: 0 } : { top: 10, right: 60, bottom: 10, left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
          <XAxis
            dataKey="course_distance_m"
            tickFormatter={(v) => `${(v / 1000).toFixed(0)}`}
            tick={{ fill: colors.grey[400], fontSize: isMobile ? 9 : 11 }}
          />
          <YAxis
            yAxisId="gap"
            tickFormatter={(v) => formatTime(v)}
            tick={isMobile ? false : { fill: colors.grey[400], fontSize: 11 }}
            width={isMobile ? 0 : 60}
            axisLine={!isMobile}
            tickLine={!isMobile}
          />
          <YAxis
            yAxisId="alt"
            orientation="right"
            domain={[altMin - 10, altMax + 10]}
            tick={isMobile ? false : { fill: colors.grey[600], fontSize: 10 }}
            width={isMobile ? 0 : 50}
            axisLine={!isMobile}
            tickLine={!isMobile}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine yAxisId="gap" y={0} stroke={colors.grey[400]} strokeWidth={2} />
          <Area
            yAxisId="gap"
            type="monotone"
            dataKey="gap_pos"
            fill="rgba(34,197,94,0.2)"
            stroke="none"
          />
          <Area
            yAxisId="gap"
            type="monotone"
            dataKey="gap_neg"
            fill="rgba(239,68,68,0.2)"
            stroke="none"
          />
          <Line
            yAxisId="gap"
            type="monotone"
            dataKey="time_delta_s"
            stroke="#22c55e"
            dot={false}
            strokeWidth={2}
          />
          <Line
            yAxisId="alt"
            type="monotone"
            dataKey="altitude_a"
            stroke={colors.grey[600]}
            dot={false}
            strokeWidth={1}
            opacity={0.5}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}
